import React, { useEffect, useState } from "react";
import { InputGroup, Form, Collapse } from "react-bootstrap";
import numeral from "numeral";
import CurrencyInput from "react-currency-input-field";
import { IconButton, Tooltip } from "@mui/material";
import usePalette from "../../../../../../hooks/usePalette";
import { useDispatch, useSelector } from "react-redux";

// Icons
import Icon from "@mdi/react";
import {
  mdiNoteTextOutline,
  mdiPencil,
  mdiCheck,
  mdiClose,
  mdiMenuDown,
  mdiMenuRight,
} from "@mdi/js";

import { updateCertifyFinancialItem } from "../../../../../../redux/slices/utilsSlice";

const TableBody = ({
  data,
  showColumn,
  colSpan,
  project,
  matchCell,
  newValues,
  certifiedId,
  handleEditAmount,
  showAllYear,
  showYears,
  handleShow,
  viewColumns,
}) => {
  const palette = usePalette();
  const dispatch = useDispatch();

  const [ceilingRequired, setCeilingRequired] = useState(0);
  const [upenColumn, setUpenColumn] = useState(0);
  const [jppnColumn, setJppnColumn] = useState(0);
  const [cabinetColumn, setCabinetColumn] = useState(0);

  const { certifyFinancial } = useSelector((state) => ({
    certifyFinancial: state.utilsReducer.certifyFinancial,
  }));

  const handleChangeAmount = (value, obj) => {
    const props = {
      field: "ceiling-flow",
      data: {
        id: obj.id,
        amount: value,
      },
    };
    dispatch(updateCertifyFinancialItem(props));
  };

  useEffect(() => {
    const editProcess = data?.flatMap((obj) => obj?.children);

    const processStages = editProcess?.flatMap(
      (obj) => obj?.ceiling_flow_detail_stages,
    );

    const required = processStages
      ?.filter((obj) => obj?.stages === "4" && obj?.status === "2")
      ?.reduce((acc, obj) => acc + parseFloat(obj?.amount), 0);
    setCeilingRequired(required);

    if (project.stages === "4") {
      const upenEdit = editProcess?.reduce(
        (acc, obj) => acc + parseFloat(obj?.amount),
        0,
      );
      setUpenColumn(upenEdit);
    }
    if (project.stages === "5") {
      const certifiedUpen = processStages
        ?.filter((obj) => obj?.stages === "5" && obj?.status === "3")
        ?.reduce((acc, obj) => acc + parseFloat(obj?.amount), 0);
      const jppnEdit = editProcess?.reduce(
        (acc, obj) => acc + parseFloat(obj?.amount),
        0,
      );
      setUpenColumn(certifiedUpen);
      setJppnColumn(jppnEdit);
    }
    if (project.stages === "6") {
      const certifiedUpen = processStages
        ?.filter((obj) => obj?.stages === "5" && obj?.status === "3")
        ?.reduce((acc, obj) => acc + parseFloat(obj?.amount), 0);
      const certifiedJppn = processStages
        ?.filter((obj) => obj?.stages === "6" && obj?.status === "3")
        ?.reduce((acc, obj) => acc + parseFloat(obj?.amount), 0);
      const cabinetEdit = editProcess?.reduce(
        (acc, obj) => acc + parseFloat(obj?.amount),
        0,
      );
      setUpenColumn(certifiedUpen);
      setJppnColumn(certifiedJppn);
      setCabinetColumn(cabinetEdit);
    }
  }, [data, project]);

  return (
    <tbody style={{ fontWeight: 500 }}>
      {data
        .toSorted((a, b) => a.year - b.year)
        .map((item) => {
          const { children } = item;

          const calculateTotal = (stages, status) => {
            const ceilingFlowStages = children?.flatMap(
              (obj) => obj?.ceiling_flow_detail_stages,
            );
            const filteredStages = ceilingFlowStages?.filter(
              (obj) =>
                obj.stages === stages.toString() &&
                obj.status === status.toString(),
            );
            const sum = filteredStages?.reduce(
              (sum, obj) => (sum += parseFloat(obj.amount)),
              0,
            );

            return sum;
          };

          const initialCeilingRequested = calculateTotal(4, 2);
          const certifiedByUpen = calculateTotal(5, 3);
          const certifiedByJppn = calculateTotal(6, 3);

          const calculateEditedTotal = (stages, status) => {
            const findYearData = data.find(
              (obj) => obj.year === Number(item.year),
            );

            const sum = findYearData?.children?.reduce((total, obj) => {
              return (total += parseFloat(obj.amount, 10));
            }, 0);

            return sum;
          };

          const editAmount = calculateEditedTotal();

          return (
            <React.Fragment key={item.year}>
              <tr
                className='custom-table-head'
                onClick={() => handleShow(item.year)}
                style={{ cursor: "pointer" }}
              >
                <td className='text-center' style={{ borderRight: "none" }}>
                  {showYears.includes(item.year) ? (
                    <Icon className='m-0' path={mdiMenuDown} size={1} />
                  ) : (
                    <Icon className='m-0' path={mdiMenuRight} size={1} />
                  )}
                </td>
                <td colSpan={2 + colSpan} style={{ borderLeft: "none" }}>
                  <span
                    className='text-uppercase'
                    style={{ fontSize: 13, fontWeight: 700 }}
                  >
                    Tahun {item.year}
                  </span>
                </td>
              </tr>
              {item.children.map((child, idx) => {
                const upenStages = child.ceiling_flow_detail_stages.find(
                  (obj) => obj?.stages === "4" && obj?.status === "2",
                );
                const jppnStages = child.ceiling_flow_detail_stages.find(
                  (obj) => obj?.stages === "5" && obj?.status === "3",
                );
                const cabinetStages = child.ceiling_flow_detail_stages.find(
                  (obj) => obj?.stages === "6" && obj?.status === "3",
                );
                const cabinetApproved = child.ceiling_flow_detail_stages.find(
                  (obj) => obj?.stages === "6" && obj?.status === "4",
                );
                const initialCeilingRequested =
                  child.ceiling_flow_detail_stages.filter(
                    (obj) => obj?.stages === "4" && obj?.status === "2",
                  );

                const certifiedCeilingByUpen =
                  child.ceiling_flow_detail_stages.filter(
                    (obj) => obj?.stages === "5" && obj?.status === "3",
                  );

                return (
                  <tr
                    key={child.id}
                    className='collapse-containing-div text-truncate'
                  >
                    <td className='text-center'>
                      <Collapse in={showYears.includes(item.year)}>
                        <div>{idx + 1}.</div>
                      </Collapse>
                    </td>
                    <td>
                      <Collapse in={showYears.includes(item.year)}>
                        <div style={{ textWrap: "wrap" }}>
                          <p className='p-2 mb-0'>
                            {child.cost_description?.toLocaleUpperCase()}
                          </p>
                        </div>
                      </Collapse>
                    </td>

                    {viewColumns.dipohon.checked && (
                      <td className='text-end  text-truncate'>
                        <Collapse in={showYears.includes(item.year)}>
                          <div className='px-2'>
                            {`${numeral(upenStages?.amount).format("0,0.00")}`}
                          </div>
                        </Collapse>
                      </td>
                    )}

                    {/* UPEN Section */}
                    {viewColumns.upen.checked && (
                      <td className='text-end'>
                        <Collapse in={showYears.includes(item.year)}>
                          <div>
                            {project.stages === "4" ? (
                              <InputGroup className='d-flex justify-content-end'>
                                <Form.Control
                                  className={`text-end text-truncate m-1`}
                                  as={CurrencyInput}
                                  min={0}
                                  name='amount'
                                  autoComplete='off'
                                  decimalsLimit={2}
                                  decimalScale={2}
                                  value={child?.amount}
                                  onValueChange={(value) =>
                                    handleChangeAmount(value, child)
                                  }
                                />
                              </InputGroup>
                            ) : (
                              <React.Fragment>
                                {`${numeral(jppnStages?.amount).format(
                                  "0,0.00",
                                )}`}
                              </React.Fragment>
                            )}
                          </div>
                        </Collapse>
                      </td>
                    )}

                    {/* JPPN SECTION */}
                    {showColumn.jppn && (
                      <>
                        {viewColumns.jppn.checked && (
                          <td className='text-end'>
                            <Collapse in={showYears.includes(item.year)}>
                              <div>
                                {project.stages === "5" ? (
                                  <InputGroup className='d-flex justify-content-end'>
                                    <Form.Control
                                      className={`text-end text-truncate m-1`}
                                      as={CurrencyInput}
                                      min={0}
                                      name='amount'
                                      autoComplete='off'
                                      decimalsLimit={2}
                                      decimalScale={2}
                                      value={child?.amount}
                                      onValueChange={(value) =>
                                        handleChangeAmount(value, child)
                                      }
                                    />
                                  </InputGroup>
                                ) : (
                                  <React.Fragment>
                                    {`${numeral(cabinetStages?.amount).format(
                                      "0,0.00",
                                    )}`}
                                  </React.Fragment>
                                )}
                              </div>
                            </Collapse>
                          </td>
                        )}
                      </>
                    )}

                    {/* CABINET SECTION */}
                    {showColumn.cabinet && (
                      <td className='text-end'>
                        <Collapse in={showYears.includes(item.year)}>
                          <div>
                            {project.stages === "6" ? (
                              <InputGroup className='d-flex justify-content-end'>
                                <Form.Control
                                  className={`text-end text-truncate m-1`}
                                  as={CurrencyInput}
                                  min={0}
                                  name='amount'
                                  autoComplete='off'
                                  decimalsLimit={2}
                                  decimalScale={2}
                                  value={child?.amount}
                                  onValueChange={(value) =>
                                    handleChangeAmount(value, child)
                                  }
                                />
                              </InputGroup>
                            ) : (
                              <React.Fragment>
                                {`${numeral(cabinetApproved?.amount).format(
                                  "0,0.00",
                                )}`}
                              </React.Fragment>
                            )}
                          </div>
                        </Collapse>
                      </td>
                    )}
                  </tr>
                );
              })}
              <tr>
                <td
                  colSpan={2}
                  className='text-end '
                  style={{ fontWeight: 700 }}
                >
                  JUMLAH SILING TAHUN {item.year}
                </td>
                {viewColumns.dipohon.checked && (
                  <td className='text-end ' style={{ fontWeight: 700 }}>
                    <span style={{ marginRight: "0.2rem" }}>
                      {numeral(initialCeilingRequested).format("0,0.00")}
                    </span>
                  </td>
                )}

                {viewColumns.upen.checked && (
                  <td className='text-end ' style={{ fontWeight: 700 }}>
                    <span style={{ marginRight: "0.2rem" }}>
                      {showColumn.jppn ? (
                        <>{numeral(certifiedByUpen).format("0,0.00")}</>
                      ) : (
                        <>{numeral(editAmount).format("0,0.00")}</>
                      )}
                    </span>
                  </td>
                )}

                {showColumn.jppn && (
                  <>
                    {viewColumns.jppn.checked && (
                      <td className='text-end ' style={{ fontWeight: 700 }}>
                        <span style={{ marginRight: "0.2rem" }}>
                          {showColumn.cabinet ? (
                            <>{numeral(certifiedByJppn).format("0,0.00")}</>
                          ) : (
                            <>{numeral(editAmount).format("0,0.00")}</>
                          )}
                        </span>
                      </td>
                    )}
                  </>
                )}
                {showColumn.cabinet && (
                  <td className='text-end ' style={{ fontWeight: 700 }}>
                    <span style={{ marginRight: "0.2rem" }}>
                      <>{numeral(editAmount).format("0,0.00")}</>
                    </span>
                  </td>
                )}
              </tr>
            </React.Fragment>
          );
        })}
      <tr style={{ borderTop: "double" }}>
        <td colSpan={2} className='text-end text-uppercase'>
          Jumlah Aliran Siling
        </td>
        <td className='text-end' style={{ width: "13%" }}>
          <span style={{ marginRight: "0.2rem" }}>
            {numeral(ceilingRequired).format("0,0.00")}
          </span>
        </td>
        <td className='text-end' style={{ width: "13%" }}>
          <span style={{ marginRight: "0.2rem" }}>
            {numeral(upenColumn).format("0,0.00")}
          </span>
        </td>
        {showColumn.jppn && (
          <td className='text-end' style={{ width: "13%" }}>
            <span style={{ marginRight: "0.2rem" }}>
              {numeral(jppnColumn).format("0,0.00")}
            </span>
          </td>
        )}
        {showColumn.cabinet && (
          <td className='text-end' style={{ width: "13%" }}>
            <span style={{ marginRight: "0.2rem" }}>
              {numeral(cabinetColumn).format("0,0.00")}
            </span>
          </td>
        )}
      </tr>
    </tbody>
  );
};

export default TableBody;
